import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { DashboardCardFooterContext } from './DashboardCard'
import BarChartIcon from '@mui/icons-material/BarChartOutlined'
import LineChartIcon from '@mui/icons-material/SsidChartOutlined'
import PieChartIcon from '@mui/icons-material/PieChartOutlineOutlined'
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import Modal from '../common/Modal'
import Tooltip from '../common/Tooltip'
import { getCardControls } from '../../helpers/visualisationDefaults'
import { useEmptyStateContext } from '../../StateManagment/EmptyStateContext'
import InlineDetailsContext from '../../StateManagment/InlineDetailsContext'

const GRAPH_TYPE_LABELS = { bar: 'Staafdiagram', line: 'Lijngrafiek', pie: 'Taartdiagram', radar: 'Radardiagram', sankey: 'Transities' }

// Output cards use the line/bar graph-type control as a cumulative/individual
// view toggle, so they get their own semantic labels.
const GRAPH_TYPE_LABELS_BY_FIELD_TYPE = {
    output: { line: 'Cumulatief', bar: 'Losse resultaten' },
}

const GraphTypeIcon = ({ type }) => {
    if (type === 'line') return <LineChartIcon />
    if (type === 'pie') return <PieChartIcon />
    if (type === 'radar') return <RadarOutlinedIcon />
    if (type === 'sankey') return <AccountTreeOutlinedIcon />
    return <BarChartIcon />
}

const ViewIcon = ({ viewId }) => {
    if (viewId === 'list') return <ViewListOutlinedIcon />
    return <ViewDayOutlinedIcon />
}

const updateItem = (docid, patch) =>
    db.collection('DashboardItems').doc(docid).update({
        ...patch,
        Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    })

/**
 * @typedef {object} CardAction
 * @property {string} id - Sleutel voor React
 * @property {string} label - Wat de knop doet; ook de tooltip en de naam voor een schermlezer
 * @property {Function} Icon - Het MUI-icoon dat in de balk staat
 * @property {() => void} onClick - Wat er gebeurt bij een klik
 */

/**
 * @param {object} props
 * @param {object} props.item - Het DashboardItem-document
 * @param {string} [props.fieldType] - Soort veld, bepaalt welke grafiektypes er zijn
 * @param {string} [props.currentGraphType] - Het gekozen grafiektype
 * @param {Array<object>} [props.subItems] - Subitems van de kaart
 * @param {(patch: object) => Promise<void>} [props.onSave] - Eigen opslaan in plaats van het DashboardItem bijwerken
 * @param {React.ReactNode} [props.leadingBadge] - Teller vooraan in de balk, alleen zonder onderbalk
 * @param {Array<CardAction>} [props.extraActions] - Eigen handelingen van de kaart, als iconen achter 'Weergave aanpassen' (de agendakaart: 'Huishoudens inplannen')
 */
const DashboardCardOptions = ({
    item,
    fieldType,
    currentGraphType,
    subItems = [],
    onSave,
    leadingBadge,
    extraActions = [],
}) => {
    const controls = getCardControls(item, { fieldType })
    const footerEl = useContext(DashboardCardFooterContext)
    // Deep-analysis pages show labelled buttons instead of bare icons.
    const onDeepAnalysis = useContext(InlineDetailsContext)
    // The sankey graph type needs persona-paired transitions; MatrixData
    // publishes availability via the empty-state context. Hide it otherwise.
    const { sankeyAvailable } = useEmptyStateContext()

    const [graphTypeModalOpen, setGraphTypeModalOpen] = useState(false)
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [pendingGraphType, setPendingGraphType] = useState(currentGraphType || 'bar')
    const [pendingView, setPendingView] = useState(
        item?.[controls.view?.field] || controls.view?.defaultValue || ''
    )

    const persist = async (patch) => {
        if (onSave) {
            await onSave(patch)
            return
        }
        if (!item?.docid) return
        await updateItem(item.docid, patch)
    }

    const allowedGraphTypes = Array.isArray(controls.graphTypes)
        ? controls.graphTypes.filter((type) => type !== 'sankey' || sankeyAvailable)
        : controls.graphTypes
    const showGraphType = Array.isArray(allowedGraphTypes) && allowedGraphTypes.length > 0
    const showView = !!controls.view
    const showActions = extraActions.length > 0

    if (!showGraphType && !showView && !showActions) return null

    // ── Graph type ──
    const isOutputViewToggle = fieldType === 'output'
    const graphTypeModalHeader = isOutputViewToggle ? 'Weergave aanpassen' : 'Grafiektype aanpassen'
    const graphTypeLabel = (type) =>
        GRAPH_TYPE_LABELS_BY_FIELD_TYPE[fieldType]?.[type] || GRAPH_TYPE_LABELS[type]

    const openGraphTypeModal = () => {
        setPendingGraphType(currentGraphType || allowedGraphTypes?.[0] || 'bar')
        setGraphTypeModalOpen(true)
    }

    const saveGraphType = async () => {
        await persist({ GraphType: pendingGraphType })
        setGraphTypeModalOpen(false)
    }

    const graphTypeContent = (
        <div className='dashboard-card-options-modal-body'>
            {(allowedGraphTypes || []).map(type => (
                <label key={type} className='dashboard-card-options-graph-type-choice'>
                    <input
                        type='radio'
                        name='graphType'
                        value={type}
                        checked={pendingGraphType === type}
                        onChange={(e) => setPendingGraphType(e.target.value)}
                    />
                    <GraphTypeIcon type={type} />
                    <span>{graphTypeLabel(type)}</span>
                </label>
            ))}
        </div>
    )

    // ── View picker ──
    const openViewModal = () => {
        setPendingView(item?.[controls.view.field] || controls.view.defaultValue)
        setViewModalOpen(true)
    }

    const saveView = async () => {
        await persist({ [controls.view.field]: pendingView })
        setViewModalOpen(false)
    }

    const viewContent = (
        <div className='dashboard-card-options-modal-body'>
            {(controls.view?.options || []).map(opt => (
                <label key={opt.id} className='dashboard-card-options-graph-type-choice'>
                    <input
                        type='radio'
                        name='agendaView'
                        value={opt.id}
                        checked={pendingView === opt.id}
                        onChange={(e) => setPendingView(e.target.value)}
                    />
                    <ViewIcon viewId={opt.id} />
                    <span>{opt.label}</span>
                </label>
            ))}
        </div>
    )

    const currentViewId = item?.[controls.view?.field] || controls.view?.defaultValue

    // Labelled buttons for the deep-analysis control bar — same modals, the
    // trigger just says what it does (design: "was 3 mystery icons").
    const labelledBar = (
        <div className='dashboard-card-options deep-analysis-ctrl-group'>
            {showGraphType && (
                <button type='button' className='deep-analysis-ctrl' onClick={openGraphTypeModal}>
                    <GraphTypeIcon type={currentGraphType || allowedGraphTypes[0]} />
                    <span>{isOutputViewToggle ? 'Weergave' : 'Grafiektype'}</span>
                    <ExpandMoreIcon className='deep-analysis-ctrl-chevron' />
                </button>
            )}
            {showView && (
                <button type='button' className='deep-analysis-ctrl' onClick={openViewModal}>
                    <ViewIcon viewId={currentViewId} />
                    <span>Weergave</span>
                    <ExpandMoreIcon className='deep-analysis-ctrl-chevron' />
                </button>
            )}
            {extraActions.map(({ id, label, Icon, onClick }) => (
                <button key={id} type='button' className='deep-analysis-ctrl' onClick={onClick}>
                    <Icon />
                    <span>{label}</span>
                </button>
            ))}
        </div>
    )

    const iconBar = (
        <div className='dashboard-card-options'>
            {/* maxWidth, geen vaste `top`: een tekst die niet in de breedte van
                het icoon past, loopt over twee regels en lag met een vaste
                afstand naar boven óver het icoon. Met maxWidth meet de tooltip
                zijn eigen hoogte en gaat hij onder of boven het icoon staan. */}
            {showGraphType && (
                <Tooltip content={graphTypeModalHeader} width='15px' maxWidth='200px'>
                    <span className='dashboard-card-options-icon' onClick={openGraphTypeModal}>
                        {/* Eén vast icoon: een icoon dat meewisselt met het
                            gekozen type las als een tweede grafiek. In het
                            keuzevenster houdt elke optie haar eigen icoon. */}
                        <InsertChartOutlinedIcon />
                    </span>
                </Tooltip>
            )}
            {showView && (
                <Tooltip content='Weergave aanpassen' width='15px' maxWidth='200px'>
                    <span className='dashboard-card-options-icon' onClick={openViewModal}>
                        <ViewIcon viewId={currentViewId} />
                    </span>
                </Tooltip>
            )}
            {/* Een echte knop met een naam: de tooltip is een extraatje, niet
                de enige drager van wat dit icoon doet.
                AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile */}
            {extraActions.map(({ id, label, Icon, onClick }) => (
                <Tooltip key={id} content={label} width='15px' maxWidth='200px'>
                    <button type='button' className='dashboard-card-options-icon' aria-label={label} onClick={onClick}>
                        <Icon />
                    </button>
                </Tooltip>
            ))}
            {/* Inline fallback only: cards with a bottom bar already show the
                x / y counter via CardHeaderCounter's counter slot. */}
            {leadingBadge && !footerEl && (
                <span className='dashboard-card-options-badge'>{leadingBadge}</span>
            )}
        </div>
    )

    const bar = onDeepAnalysis ? labelledBar : iconBar

    // Modals render to a react-modal portal (outside this tree) regardless
    // of where we place them; they stay in-line.
    const modals = (
        <>
            <Modal
                modalOpen={graphTypeModalOpen}
                setModalOpen={setGraphTypeModalOpen}
                confirm={saveGraphType}
                pageHeader={graphTypeModalHeader}
                content={graphTypeContent}
            />

            <Modal
                modalOpen={viewModalOpen}
                setModalOpen={setViewModalOpen}
                confirm={saveView}
                pageHeader='Weergave aanpassen'
                content={viewContent}
            />
        </>
    )

    return (
        <>
            {footerEl ? createPortal(bar, footerEl) : bar}
            {modals}
        </>
    )
}

export default DashboardCardOptions
